import React, { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  icon?: React.ReactNode;
  inputSize?: 'sm' | 'md' | 'lg';
  variant?: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'tel' | 'url';
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      icon,
      inputSize = 'md',
      variant = 'text',
      fullWidth = false,
      className = '',
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const isTextarea = variant === 'textarea';

    // Size styles
    const sizeStyles = {
      sm: 'px-3 py-2 text-sm min-h-[40px]',
      md: 'px-4 py-2.5 text-base min-h-touch',
      lg: 'px-5 py-3 text-lg min-h-[52px]',
    };

    // Base input styles
    const baseStyles = `
      w-full rounded-xl border-2 transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-0
      disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-surface
      ${sizeStyles[inputSize]}
      ${icon ? 'pl-10' : ''}
      ${error 
        ? 'border-danger focus:border-danger focus:ring-danger/20' 
        : 'border-default focus:border-strong focus:ring-accent/20'
      }
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const TextareaStyles = `
      ${baseStyles}
      min-h-[120px] resize-y
    `;

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {/* Label */}
        {label && (
          <label className="block text-sm font-semibold text-txt-secondary mb-2">
            {label}
            {required && <span className="text-danger ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Icon */}
          {icon && !isTextarea && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none">
              {icon}
            </div>
          )}

          {/* Input or Textarea */}
          {isTextarea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              className={TextareaStyles}
              disabled={disabled}
              required={required}
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              type={variant}
              className={baseStyles}
              disabled={disabled}
              required={required}
              {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            />
          )}

          {/* Error Icon */}
          {error && !isTextarea && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-danger">
              <AlertCircle className="w-5 h-5" />
            </div>
          )}
        </div>

        {/* Helper Text or Error Message */}
        {(helperText || error) && (
          <div className="mt-2">
            {error ? (
              <p className="text-sm text-danger flex items-start gap-1.5">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </p>
            ) : helperText ? (
              <p className="text-sm text-txt-tertiary">{helperText}</p>
            ) : null}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

